import { useEffect, useRef } from "react";
import Plotly from "plotly.js-dist-min";
import { RACE_COLORS, FONT_FAMILY, IS_TOUCH, BAR_HEIGHT, BAR_PADDING } from "../constants.js";

const RACES = ["BLACK", "WHITE"];

export default function BarChartRace({ data, title, subtitle, sortOrder, dynamicRange }) {
  const ref = useRef();

  useEffect(() => {
    if (!data || !data.length) return;

    const allCats = sortOrder || [...new Set(data.map(d => d.category_value))];
    // Only keep categories that actually exist in the data (e.g. drop NOT RECORDED when filtered out)
    const cats = allCats.filter(cat => data.some(d => d.category_value === cat));
    const n = cats.length;
    const height = n * BAR_HEIGHT * 2 + BAR_PADDING;

    // Dynamic range only when explicitly requested (e.g. Juvenile chart); otherwise fixed 0–110
    const xMax = dynamicRange
      ? (() => {
          const allVals = data.map(d => d.pct_race).filter(v => v != null);
          const dataMax = allVals.length ? Math.max(...allVals) : 100;
          return Math.min(115, Math.ceil((dataMax + 2) / 5) * 5);
        })()
      : 110;

    const traces = RACES.map(race => {
      const raceData = data.filter(d => d.race === race);
      const map = Object.fromEntries(raceData.map(d => [d.category_value, d.pct_race]));
      return {
        type: "bar",
        orientation: "h",
        name: race,
        x: cats.map(c => map[c] != null ? map[c] : null),
        y: cats,
        marker: { color: RACE_COLORS[race] || "#aaaaaa" },
        text: cats.map(c => map[c] != null ? `${map[c]}%` : ""),
        textposition: "outside",
        textfont: { size: 13, family: FONT_FAMILY },
        cliponaxis: false,
        hovertemplate: `<b>${race}</b><br>%{y}: %{x:.1f}%<extra></extra>`,
      };
    });

    Plotly.react(ref.current, traces, {
      barmode: "group",
      title: { text: `<b>${title}</b>`, font: { size: 18, color: "#333333", family: FONT_FAMILY }, x: 0, xanchor: "left" },
      height,
      margin: { l: 140, r: 100, t: 60, b: 60 },
      bargap: 0.15,
      bargroupgap: 0.1,
      xaxis: {
        title: "% of stops",
        ticksuffix: "%",
        range: [0, xMax],
        tickfont: { size: 13, family: FONT_FAMILY },
      },
      yaxis: {
        tickfont: { size: 14, family: FONT_FAMILY },
        automargin: true,
      },
      font: { family: FONT_FAMILY },
      paper_bgcolor: "white",
      plot_bgcolor: "white",
      showlegend: false,
    }, { responsive: true, displayModeBar: false, staticPlot: IS_TOUCH });
  }, [data, title, subtitle, sortOrder]);

  if (!data || !data.length) return null;
  if (data.every(d => d.pct_race == null || d.pct_race === 0)) return null;
  return (
    <div>
      {subtitle && <p style={{ fontFamily: FONT_FAMILY, fontSize: 14, fontStyle: "italic", color: "#555", margin: "4px 0 0" }}>{subtitle}</p>}
      <div className="chart-scroll-x">
        <div ref={ref} style={{ minWidth: 600 }} />
      </div>
    </div>
  );
}
